import React, { useMemo } from "react";
import CometShower from "./CometShower";
import { useScene } from "./scene/SceneContext";

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function WillowFluffRain({ count = 46, className = "" } = {}) {
  const { variant } = useScene();
  const isNight = variant === "night";

  const drops = useMemo(() => {
    if (isNight) return [];
    const safeCount = clamp(count, 10, 120);
    return Array.from({ length: safeCount }).map((_, i) => {
      const left = Math.random() * 100; // vw%
      const delay = Math.random() * 8; // s
      const duration = 9 + Math.random() * 10; // s
      const size = 10 + Math.random() * 14; // px
      const opacity = 0.35 + Math.random() * 0.5; // 0.35 - 0.85
      const blur = Math.random() < 0.35 ? 0.6 + Math.random() * 0.9 : 0; // px
      const drift = (Math.random() - 0.5) * 120; // px
      const sway = 10 + Math.random() * 22; // px
      const rot = 90 + Math.random() * 220; // deg
      return { left, delay, duration, size, opacity, blur, drift, sway, rot };
    });
  }, [count, isNight]);

  if (isNight) {
    // Night scene: replace willow fluff with comets.
    return <CometShower count={1} className={className} />;
  }

  return (
    <div className={`willow-fluff-rain ${className}`} aria-hidden="true">
      <style>{`
        .willow-fluff-rain {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
        }

        .willow-fluff-rain__drop {
          position: absolute;
          top: -12vh;
          will-change: transform, opacity;
          animation-name: willow-fluff-fall;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          mix-blend-mode: screen;
        }

        .willow-fluff-rain__fluff {
          position: absolute;
          inset: 0;
          border-radius: 999px;
          background:
            radial-gradient(circle at 32% 38%, rgba(255,255,255,0.95) 0 42%, rgba(255,255,255,0) 68%),
            radial-gradient(circle at 70% 52%, rgba(255,255,255,0.90) 0 40%, rgba(255,255,255,0) 66%),
            radial-gradient(circle at 48% 72%, rgba(255,255,255,0.75) 0 36%, rgba(255,255,255,0) 68%);
          filter: drop-shadow(0 10px 14px rgba(0,0,0,0.06));
          will-change: transform;
          animation: willow-fluff-sway 2.9s ease-in-out infinite alternate;
        }

        @keyframes willow-fluff-fall {
          0%   { transform: translate3d(0, -12vh, 0) rotate(0deg); }
          100% { transform: translate3d(var(--wf-drift, 0px), 118vh, 0) rotate(var(--wf-rot, 180deg)); }
        }

        @keyframes willow-fluff-sway {
          0%   { transform: translate3d(0, 0, 0) rotate(0deg); }
          100% { transform: translate3d(var(--wf-sway, 14px), -6px, 0) rotate(18deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .willow-fluff-rain__drop { animation: none !important; display: none; }
        }
      `}</style>

      {drops.map((d, idx) => (
        <span
          key={idx}
          className="willow-fluff-rain__drop"
          style={{
            left: `${d.left}vw`,
            width: `${d.size}px`,
            height: `${d.size}px`,
            opacity: d.opacity,
            filter: d.blur ? `blur(${d.blur}px)` : undefined,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.duration}s`,
            "--wf-drift": `${d.drift}px`,
            "--wf-sway": `${d.sway}px`,
            "--wf-rot": `${d.rot}deg`,
          }}
        >
          <span className="willow-fluff-rain__fluff" />
        </span>
      ))}
    </div>
  );
}

